import { DIVISIONS } from '@multirx/shared/types/division.js';
import type { Circuit } from '@multirx/shared/types/track.js';
import type { RaceHistoryDetail, RaceHistorySummary } from '@multirx/shared/types/raceHistory.js';
import {
  clampReplayRound,
  getReplayDiceRollCount,
  getReplayRoundCount,
  resolveRowsForRound,
  resolveReplayCircuit,
  mergeReplayCircuit as attachCircuitToReplay,
} from '@multirx/shared/game/archiveReplay.js';
import { fetchCircuitById } from '../circuits/api.js';
import { circuitDisplayTitle } from '../circuits/circuitDisplay.js';
import { labelOwnDriver } from '../auth/ownDriverLabel.js';
import { nationalityFlagMarkup } from '../components/nationalityFlag.js';
import { replaceAppHash } from '../ui/appHash.js';
import { createPageShell } from '../ui/pageShell.js';
import { createPageEmptyState } from '../ui/pageEmptyState.js';
import { createPillButton } from '../ui/pillButton.js';
import { createArchiveStepButton } from '../ui/archiveStepButton.js';
import { el } from '../ui/dom.js';
import { openConfirmModal } from '../ui/confirmModal.js';
import { mountEarthLoaderOverlay, mountEarthLoaderPage } from '../ui/earthLoader.js';
import {
  deleteRaceHistory,
  fetchRaceHistoriesPage,
  fetchRaceHistory,
  ARCHIVE_LIST_PAGE_SIZE,
} from './api.js';
import { createArchiveRaceCard } from './archiveListCards.js';
import { ensurePhaserReadyForPlay } from '../game/phaserApp.js';
import { mountArchiveReplayBoard, type ArchiveReplayController } from './ArchiveReplayBoard.js';
import {
  buildArchiveListTooltipHtml,
  hideArchiveListTooltip,
  removeArchiveListTooltip,
  showArchiveListTooltip,
} from './archiveListTooltip.js';
import { removeArchiveRosterTooltip, renderArchiveRoster } from './archiveReplayDisplay.js';
import { formatTurnHistoryRollMarkup } from '../play/turnHistoryRoll.js';
import { shouldFillViewportWithNextPage } from './archivesFillViewport.js';
import {
  ARCHIVE_ROUND_MS_MAX,
  ARCHIVE_ROUND_MS_MIN,
  readArchiveRoundMs,
  writeArchiveRoundMs,
} from './archiveRoundDuration.js';
import {
  clearArchiveReturnTo,
  consumeArchiveAutoplayFlag,
  consumePendingQualRecaps,
  peekArchiveReturnTo,
} from './archivePostSimulateNav.js';
import { showChampionshipQualCompletionRecaps } from '../championship/showChampionshipQualCompletionRecaps.js';
import {
  archivesDetailHash,
  archivesListHash,
  isArchivesEmbedded,
  parseReplayIdFromHash,
  setArchivesEmbedded,
} from './archivesHash.js';

let activeReplayController: ArchiveReplayController | null = null;
let stopArchiveAutoplay: (() => void) | null = null;

export type ArchivesMountOptions = {
  embedded?: boolean;
};

function destroyActiveReplay(): void {
  stopArchiveAutoplay?.();
  stopArchiveAutoplay = null;
  activeReplayController?.destroy();
  activeReplayController = null;
  removeArchiveRosterTooltip();
  removeArchiveListTooltip();
}

/** À appeler quand on quitte la vue Archives (canvas rendu au plateau Jouer). */
export function teardownArchiveReplay(): void {
  destroyActiveReplay();
  ensurePhaserReadyForPlay();
}

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

function formatArchiveRaceItemCircuit(item: RaceHistorySummary): string {
  if (item.circuitId == null) return 'Circuit non enregistré';
  return circuitDisplayTitle({ id: item.circuitId, name: item.circuitName });
}

function divisionLabel(id: number): string {
  return DIVISIONS.find((d) => d.id === id)?.shortLabel ?? `D${id}`;
}

function playModeLabel(mode: string): string {
  if (mode === 'multi-host') return 'Multijoueur (hôte)';
  if (mode === 'multi-join') return 'Multijoueur (invité)';
  return 'Solo';
}

function renderTurnPanel(
  header: HTMLElement,
  body: HTMLElement,
  detail: RaceHistoryDetail,
  selectedRound: number,
  onRoundChange: (round: number) => void,
  autoplay?: { active: boolean; onToggle: () => void; disabled?: boolean },
  duration?: { ms: number; onChange: (ms: number) => void }
): void {
  const n = detail.numParticipants;
  const total = getReplayRoundCount(detail.replay, n);
  const round = clampReplayRound(detail.replay, selectedRound, n);
  const rows = resolveRowsForRound(detail.replay, round, n);

  const applyRound = (t: number): void => {
    if (total === 0) return;
    onRoundChange(clampReplayRound(detail.replay, t, n));
  };

  header.innerHTML = '';
  header.className = 'archives-replay-card-head';

  const colPrev = el('div', {
    class: 'archives-replay-card-head__col archives-replay-card-head__col--prev',
  });
  const colTitle = el('div', {
    class: 'archives-replay-card-head__col archives-replay-card-head__col--title',
  });
  const colAutoplay = el('div', {
    class: 'archives-replay-card-head__col archives-replay-card-head__col--autoplay',
  });
  const colNext = el('div', {
    class: 'archives-replay-card-head__col archives-replay-card-head__col--next',
  });

  const prev = createArchiveStepButton('Préc.', 'prev', {
    title: 'Tour précédent',
    disabled: total <= 1 || round <= 1 || autoplay?.active === true,
    onClick: () => applyRound(round - 1),
    class: 'archives-step-btn--sm',
  });
  const next = createArchiveStepButton('Suiv.', 'next', {
    title: 'Tour suivant',
    disabled: total <= 1 || round >= total || autoplay?.active === true,
    onClick: () => applyRound(round + 1),
    class: 'archives-step-btn--sm',
  });

  colPrev.appendChild(prev);

  const title = el('h2', { class: 'archives-replay-card-head__title', text: 'Lancers de dé' });
  const turnLabel = el('span', {
    class: 'archives-replay-card-head__turn',
    text: total > 0 ? `Tour ${round} / ${total}${autoplay?.active ? ' · lecture' : ''}` : 'Tour —',
  });
  const titleLine = el('div', { class: 'archives-replay-card-head__title-line' });
  titleLine.append(title, turnLabel);
  colTitle.appendChild(titleLine);

  if (autoplay) {
    const autoplayBtn = createPillButton(autoplay.active ? 'Pause' : 'Lecture auto', {
      variant: autoplay.active ? 'soft' : 'cta',
      small: true,
      class: 'archives-replay-autoplay-btn',
    });
    if (autoplayBtn instanceof HTMLButtonElement) {
      autoplayBtn.title = autoplay.active
        ? 'Interrompre la lecture automatique'
        : 'Rejouer tous les tours depuis le tour 1';
      autoplayBtn.disabled = autoplay.disabled ?? total <= 0;
      autoplayBtn.onclick = (): void => {
        autoplay.onToggle();
      };
    }
    colAutoplay.appendChild(autoplayBtn);
  }

  if (duration) {
    const speedWrap = el('label', { class: 'archives-replay-speed' });
    const speedCaption = el('span', {
      class: 'archives-replay-speed__label',
      text: `Durée tour ${(duration.ms / 1000).toFixed(1)} s`,
    });
    const slider = document.createElement('input');
    slider.type = 'range';
    slider.className = 'archives-replay-speed__slider';
    slider.min = String(ARCHIVE_ROUND_MS_MIN);
    slider.max = String(ARCHIVE_ROUND_MS_MAX);
    slider.step = '50';
    slider.value = String(duration.ms);
    slider.setAttribute('aria-label', 'Durée d’un tour de replay');
    slider.title = 'Durée d’animation d’un tour (toutes les voitures)';
    slider.oninput = (): void => {
      const ms = writeArchiveRoundMs(Number(slider.value));
      speedCaption.textContent = `Durée tour ${(ms / 1000).toFixed(1)} s`;
      duration.onChange(ms);
    };
    speedWrap.append(speedCaption, slider);
    colAutoplay.appendChild(speedWrap);
  }

  colNext.appendChild(next);
  header.append(colPrev, colTitle, colAutoplay, colNext);

  body.innerHTML = '';
  body.className = 'archives-replay-body';

  const list = el('div', { class: 'archives-replay-list play-race-history-list' });
  if (rows.length === 0) {
    list.innerHTML = `<div class="play-race-history-empty">Aucun lancer enregistré pour ce tour.</div>`;
  } else {
    list.innerHTML = rows
      .map((row, idx) => {
        const pos = row.rollOrder ?? idx + 1;
        const p = detail.replay.participants.find((x) => x.playerIndex === row.playerIndex);
        return `<div class="play-race-history-row">
          <span class="play-race-history-pos">${pos}.</span>
          <span class="play-race-history-name">${escapeHtml(labelOwnDriver(row.driverName, p?.driverId))}</span>
          <span class="play-race-history-roll">${formatTurnHistoryRollMarkup(row)}</span>
        </div>`;
      })
      .join('');
  }

  body.appendChild(list);
}

async function resolveCircuitForReplay(detail: RaceHistoryDetail): Promise<Circuit | null> {
  const embedded = detail.replay.circuit;
  let fetched: Circuit | undefined;
  if (detail.circuitId != null) {
    try {
      fetched = (await fetchCircuitById(detail.circuitId)).circuit;
    } catch {
      fetched = undefined;
    }
  }
  return resolveReplayCircuit(embedded, fetched);
}

function showArchiveLoadError(root: HTMLElement, message: string): void {
  root.replaceChildren();
  const body = el('div', { class: isArchivesEmbedded() ? 'seasons-replays-embed' : 'page-body' });
  body.appendChild(
    createPageEmptyState({
      title: 'Impossible d’afficher ce replay',
      description: message,
      primaryAction: {
        label: 'Retour aux replays',
        href: `#${archivesListHash()}`,
        large: true,
      },
    })
  );
  if (isArchivesEmbedded()) {
    root.appendChild(body);
    return;
  }
  const { page } = createPageShell({
    title: 'Replays',
    breadcrumbs: [{ label: 'Replays', href: '#archives' }],
  });
  page.appendChild(body);
  root.appendChild(page);
}

async function renderDetail(root: HTMLElement, detail: RaceHistoryDetail): Promise<void> {
  try {
    await renderDetailContent(root, detail);
  } catch (err) {
    showArchiveLoadError(
      root,
      err instanceof Error ? err.message : 'Erreur lors du chargement de la course.'
    );
  }
}

async function renderDetailContent(root: HTMLElement, detail: RaceHistoryDetail): Promise<void> {
  destroyActiveReplay();
  root.replaceChildren();

  let selectedStep = 1;
  let replayForView = detail.replay;
  let roundDurationMs = readArchiveRoundMs();
  const returnToSeason = peekArchiveReturnTo();
  const startAutoplayOnMount = consumeArchiveAutoplayFlag();

  const goToReplayList = (): void => {
    destroyActiveReplay();
    if (isArchivesEmbedded()) {
      replaceAppHash(archivesListHash());
      mountArchivesApp(root, { embedded: true });
      return;
    }
    window.location.hash = archivesListHash();
  };

  const leaveToSeason = (): void => {
    const target = peekArchiveReturnTo() ?? 'saisons';
    clearArchiveReturnTo();
    const pending = consumePendingQualRecaps();
    destroyActiveReplay();
    window.location.hash = target;
    if (pending && (pending.qualMancheRecap || pending.qualFinalRecap)) {
      // Laisser le hash / mount saisons s’installer avant l’overlay.
      requestAnimationFrame(() => {
        showChampionshipQualCompletionRecaps(pending);
      });
    }
  };

  const backBtn = returnToSeason
    ? createPillButton('Retour à la saison', {
        variant: 'cta',
        small: true,
        class: 'archives-quit-to-season-btn',
        onClick: leaveToSeason,
      })
    : createPillButton('← Liste des replays', {
        variant: 'soft',
        small: true,
        onClick: goToReplayList,
      });

  let body: HTMLElement;
  if (isArchivesEmbedded()) {
    body = el('div', { class: 'seasons-replays-embed seasons-replays-detail' });
    const head = el('div', { class: 'seasons-replays-detail-head' });
    head.appendChild(backBtn);
    const title = el('h3', { class: 'seasons-tab-subtitle', text: `Course #${detail.id}` });
    const sub = el('p', {
      class: 'seasons-tab-meta',
      text: `${formatDate(detail.createdAt)} · ${playModeLabel(detail.playMode)} · ${detail.numLaps} tour${detail.numLaps > 1 ? 's' : ''}`,
    });
    head.append(title, sub);
    body.appendChild(head);
    root.appendChild(body);
  } else {
    const { page, body: shellBody } = createPageShell({
      title: `Course #${detail.id}`,
      subtitle: `${formatDate(detail.createdAt)} · ${playModeLabel(detail.playMode)} · ${detail.numLaps} tour${detail.numLaps > 1 ? 's' : ''}`,
      breadcrumbs: returnToSeason
        ? [{ label: 'Saisons', href: `#${returnToSeason}` }, { label: `Course #${detail.id}` }]
        : [{ label: 'Replays', href: '#archives' }, { label: `Course #${detail.id}` }],
      actions: backBtn,
      wide: true,
    });
    body = shellBody;
    root.appendChild(page);
  }

  const meta = el('div', { class: 'archives-detail-meta' });
  const roundCount = getReplayRoundCount(detail.replay, detail.numParticipants);
  const diceRollCount = getReplayDiceRollCount(detail.replay, detail.numParticipants);
  meta.innerHTML = `
    <span class="badge badge-division--${detail.division}">${divisionLabel(detail.division)}</span>
    <span>${detail.numParticipants} pilotes</span>
    <span>${roundCount} tour${roundCount > 1 ? 's' : ''} de jeu</span>
    <span>${diceRollCount} lancer${diceRollCount > 1 ? 's' : ''} de dé</span>
    ${detail.circuitId != null ? `<span>${escapeHtml(formatArchiveRaceItemCircuit(detail))}</span>` : ''}
    ${detail.winnerDriverName ? `<span>Vainqueur · <strong>${escapeHtml(detail.winnerDriverName)}</strong></span>` : ''}
  `;
  body.appendChild(meta);

  const boardCard = el('section', { class: 'archives-card archives-card--board' });
  boardCard.appendChild(el('h2', { class: 'archives-card-title', text: 'Replay sur le plateau' }));
  const replayLayout = el('div', { class: 'archives-replay-layout' });
  const boardHost = el('div', { class: 'archives-replay-board-host' });
  const rosterHost = el('aside', { class: 'archives-replay-roster' });
  replayLayout.append(boardHost, rosterHost);
  boardCard.appendChild(replayLayout);
  body.appendChild(boardCard);

  boardHost.appendChild(
    el('div', {
      class: 'archives-replay-board-loading',
      text: 'Chargement du plateau…',
    })
  );

  const grid = el('div', { class: 'archives-detail-grid' });

  const finalCard = el('section', { class: 'archives-card' });
  finalCard.appendChild(el('h2', { class: 'archives-card-title', text: 'Classement final' }));
  const finalList = el('ol', { class: 'archives-final-list' });
  for (const rank of detail.replay.finalRanking) {
    const p = detail.replay.participants.find((x) => x.playerIndex === rank.playerIndex);
    const li = el('li', { class: 'archives-final-row' });
    const flag = p?.nationality
      ? nationalityFlagMarkup(p.nationality, { className: 'archives-final-flag' })
      : '';
    li.innerHTML = `
      <span class="archives-final-pos">${rank.rank}.</span>
      <span class="archives-final-name">${flag} ${escapeHtml(labelOwnDriver(p?.driverName ?? `Pilote ${rank.playerIndex + 1}`, p?.driverId))}</span>
      <span class="archives-final-car">${escapeHtml(p?.carName ?? '')}</span>
    `;
    finalList.appendChild(li);
  }
  finalCard.appendChild(finalList);

  const replayCard = el('section', { class: 'archives-card archives-card--replay' });
  const replayHeader = el('div', { class: 'archives-replay-card-head' });
  const replayBody = el('div', { class: 'archives-replay-body' });
  replayCard.append(replayHeader, replayBody);

  let autoplayActive = false;
  let autoplayGen = 0;
  let autoplayTimer: ReturnType<typeof setTimeout> | null = null;
  let syncRoster: ((turn: number) => void) | null = null;

  const clearAutoplayTimer = (): void => {
    if (autoplayTimer != null) {
      clearTimeout(autoplayTimer);
      autoplayTimer = null;
    }
  };

  const stopAutoplay = (): void => {
    autoplayActive = false;
    autoplayGen++;
    clearAutoplayTimer();
  };

  stopArchiveAutoplay = stopAutoplay;

  const navigateRound = (round: number, fromAutoplay = false): void => {
    if (!fromAutoplay) stopAutoplay();
    if (getReplayRoundCount(replayForView, detail.numParticipants) === 0) return;
    selectedStep = clampReplayRound(replayForView, round, detail.numParticipants);
    syncRoster?.(selectedStep);
    activeReplayController?.showTurn(selectedStep);
    paintReplay();
  };

  const startAutoplay = (): void => {
    clearAutoplayTimer();
    autoplayGen++;
    autoplayActive = true;
    navigateRound(1, true);
  };

  const toggleAutoplay = (): void => {
    if (autoplayActive) {
      stopAutoplay();
      paintReplay();
      return;
    }
    startAutoplay();
  };

  const paintReplay = (): void => {
    renderTurnPanel(
      replayHeader,
      replayBody,
      { ...detail, replay: replayForView },
      selectedStep,
      (t) => navigateRound(t),
      {
        active: autoplayActive,
        onToggle: toggleAutoplay,
        disabled: getReplayRoundCount(replayForView, detail.numParticipants) <= 0,
      },
      {
        ms: roundDurationMs,
        onChange: (ms) => {
          roundDurationMs = ms;
          activeReplayController?.setRoundDurationMs(ms);
        },
      }
    );
  };
  paintReplay();

  grid.append(finalCard, replayCard);
  body.appendChild(grid);

  const delBtn = createPillButton('Supprimer ce replay', {
    variant: 'soft',
    small: true,
    class: 'archives-delete-btn',
    onClick: () => {
      void openConfirmModal(root, {
        title: 'Supprimer le replay',
        message: 'Supprimer définitivement ce replay de course ?\n\nCette action est irréversible.',
        confirmLabel: 'Supprimer',
        cancelLabel: 'Annuler',
        danger: true,
      }).then((confirmed) => {
        if (!confirmed) return;
        void deleteRaceHistory(detail.id)
          .then(() => {
            goToReplayList();
          })
          .catch((err: Error) => alert(err.message));
      });
    },
  });
  body.appendChild(delBtn);

  try {
    const circuit = await resolveCircuitForReplay(detail);
    replayForView = attachCircuitToReplay(detail.replay, circuit);
    boardHost.innerHTML = '';
    if (circuit?.path?.length) {
      const detailWithReplay = { ...detail, replay: replayForView };
      syncRoster = (step: number): void => {
        renderArchiveRoster(rosterHost, detailWithReplay, step, {
          followedPlayerIndex: activeReplayController?.getFollowedPlayerIndex() ?? null,
          onSelectPlayer: (playerIndex) => {
            activeReplayController?.followPlayer(playerIndex);
            syncRoster?.(step);
          },
        });
      };
      activeReplayController = mountArchiveReplayBoard(
        boardHost,
        circuit,
        replayForView,
        detail.numParticipants,
        selectedStep,
        detail.circuitName
      );
      activeReplayController.setRoundDurationMs(roundDurationMs);
      activeReplayController.onTurnChange((t) => {
        syncRoster?.(t);
        if (autoplayActive) return;
        selectedStep = t;
        paintReplay();
      });
      activeReplayController.onTurnSettled((round) => {
        syncRoster?.(round);
        if (!autoplayActive) return;
        const gen = autoplayGen;
        const total = getReplayRoundCount(replayForView, detail.numParticipants);
        if (round >= total) {
          stopAutoplay();
          paintReplay();
          return;
        }
        clearAutoplayTimer();
        // Enchaînement immédiat (pas de pause) pour un flux course continu.
        autoplayTimer = setTimeout(() => {
          if (!autoplayActive || autoplayGen !== gen) return;
          navigateRound(round + 1, true);
        }, 0);
      });
      syncRoster(selectedStep);
      if (startAutoplayOnMount) {
        startAutoplay();
      }
    } else {
      rosterHost.remove();
      boardHost.appendChild(
        el('p', {
          class: 'archives-replay-board-fallback',
          text: 'Circuit introuvable — le replay visuel nécessite le tracé enregistré (nouvelles courses uniquement).',
        })
      );
    }
  } catch (err) {
    boardHost.innerHTML = '';
    boardHost.appendChild(
      el('p', {
        class: 'archives-replay-board-fallback',
        text: err instanceof Error ? err.message : 'Erreur lors du chargement du replay.',
      })
    );
  }
}

function mountArchiveList(root: HTMLElement, onInitialError?: (err: unknown) => void): void {
  root.replaceChildren();
  hideArchiveListTooltip();

  let body: HTMLElement;
  if (isArchivesEmbedded()) {
    body = el('div', { class: 'seasons-replays-embed seasons-replays-list' });
    body.appendChild(
      el('p', {
        class: 'seasons-tab-lead',
        text: 'Historique des courses terminées — relisez chaque tour et chaque lancer de dé.',
      })
    );
    root.appendChild(body);
  } else {
    const { page, body: shellBody } = createPageShell({
      title: 'Replays',
      subtitle: 'Historique des courses terminées — relisez chaque tour et chaque lancer de dé.',
    });
    page.classList.add('page--archives-list');
    body = shellBody;
    root.appendChild(page);
  }

  const searchInput = document.createElement('input');
  searchInput.type = 'search';
  searchInput.className = 'input archives-search-input';
  searchInput.placeholder = 'Rechercher par circuit…';
  searchInput.setAttribute('aria-label', 'Rechercher un replay par circuit');
  searchInput.autocomplete = 'off';
  body.appendChild(searchInput);

  const scrollHost = el('div', { class: 'archives-grid-scroll' });
  const grid = el('div', { class: 'archives-grid', role: 'list' });
  const loader = el('div', {
    class: 'archives-grid-loader',
    'aria-live': 'polite',
  });
  loader.hidden = true;
  loader.innerHTML =
    '<span class="spinner" aria-hidden="true"></span><span class="archives-grid-loader-text">Chargement…</span>';
  const sentinel = el('div', { class: 'archives-grid-sentinel' });
  sentinel.setAttribute('aria-hidden', 'true');

  scrollHost.append(grid, sentinel, loader);
  body.appendChild(scrollHost);

  let offset = 0;
  let loading = false;
  let hasMore = true;
  let observer: IntersectionObserver | null = null;
  let searchTerm = '';
  /** Nettoyages des vignettes 3D des cartes actuellement affichées — voir `resetAndReload`. */
  let cardCleanups: Array<() => void> = [];
  /** Loader planète pour le premier fetch / recherche — pas pour l’infinite scroll. */
  let dismissPageLoader: (() => void) | null = null;

  const navigateToArchive = (id: number): void => {
    hideArchiveListTooltip();
    if (isArchivesEmbedded()) {
      replaceAppHash(archivesDetailHash(id));
      mountArchivesApp(root, { embedded: true });
      return;
    }
    window.location.hash = archivesDetailHash(id);
  };

  const appendItems = (items: RaceHistorySummary[]): void => {
    for (const item of items) {
      const { card, dispose } = createArchiveRaceCard(item, navigateToArchive);
      cardCleanups.push(dispose);
      const tooltipHtml = buildArchiveListTooltipHtml(item);
      const showTip = (): void => showArchiveListTooltip(card, tooltipHtml);
      const hideTip = (): void => hideArchiveListTooltip();
      card.addEventListener('mouseenter', showTip);
      card.addEventListener('focus', showTip);
      card.addEventListener('mouseleave', hideTip);
      card.addEventListener('blur', hideTip);
      grid.appendChild(card);
    }
  };

  const showEmpty = (): void => {
    hasMore = false;
    dismissPageLoader?.();
    dismissPageLoader = null;
    loader.hidden = true;
    observer?.disconnect();
    scrollHost.replaceChildren(
      searchTerm
        ? createPageEmptyState({
            title: 'Aucun résultat',
            description: `Aucun replay ne correspond à « ${searchTerm} ».`,
          })
        : createPageEmptyState({
            title: 'Aucun replay enregistré',
            description:
              "Les replays s'enregistrent automatiquement à la fin d'une partie (solo ou multi), avec chaque lancer de dé.",
            primaryAction: { label: 'Lancer une course', href: '#course', large: true },
          })
    );
  };

  const showScrollLoader = (message = 'Chargement…'): void => {
    loader.hidden = false;
    const text = loader.querySelector('.archives-grid-loader-text');
    if (text) text.textContent = message;
    else loader.textContent = message;
  };

  const hideScrollLoader = (): void => {
    loader.hidden = true;
    if (!loader.querySelector('.spinner')) {
      loader.innerHTML =
        '<span class="spinner" aria-hidden="true"></span><span class="archives-grid-loader-text">Chargement…</span>';
    }
  };

  /** Sentinelle encore visible → viewport pas remplie (fréquent en 2K / grand écran). */
  const isSentinelInViewport = (): boolean => {
    if (!sentinel.isConnected) return false;
    const rect = sentinel.getBoundingClientRect();
    return rect.top <= window.innerHeight + 320;
  };

  const checkLoadMore = (): void => {
    if (
      !shouldFillViewportWithNextPage({
        loading,
        hasMore,
        sentinelInViewport: isSentinelInViewport(),
      })
    ) {
      return;
    }
    void loadMore();
  };

  const loadMore = async (): Promise<void> => {
    if (loading || !hasMore) return;
    loading = true;
    if (offset === 0) {
      dismissPageLoader?.();
      dismissPageLoader = mountEarthLoaderOverlay(
        scrollHost,
        searchTerm.trim() ? 'Recherche des replays…' : 'Chargement des replays…'
      );
    } else {
      showScrollLoader('Chargement de la suite…');
    }
    try {
      const pageResult = await fetchRaceHistoriesPage(ARCHIVE_LIST_PAGE_SIZE, offset, searchTerm);
      if (offset === 0 && pageResult.items.length === 0) {
        showEmpty();
        return;
      }
      appendItems(pageResult.items);
      offset += pageResult.items.length;
      hasMore = pageResult.hasMore;
      if (!hasMore) {
        observer?.disconnect();
        sentinel.remove();
        hideScrollLoader();
      }
    } catch (err) {
      if (offset === 0) {
        dismissPageLoader?.();
        dismissPageLoader = null;
        onInitialError?.(err);
        return;
      }
      showScrollLoader('Impossible de charger la suite…');
      hasMore = false;
      observer?.disconnect();
    } finally {
      loading = false;
      dismissPageLoader?.();
      dismissPageLoader = null;
      // Remplir automatiquement les grands écrans (2K…) : garder le loader tant que
      // la sentinelle est encore dans le viewport, sinon l’utilisateur croit que c’est fini.
      const needsFill = shouldFillViewportWithNextPage({
        loading: false,
        hasMore,
        sentinelInViewport: isSentinelInViewport(),
      });
      if (!needsFill) hideScrollLoader();
      else showScrollLoader('Chargement de la suite…');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => checkLoadMore());
      });
    }
  };

  const resetAndReload = (term: string): void => {
    searchTerm = term;
    offset = 0;
    hasMore = true;
    loading = false;
    dismissPageLoader?.();
    dismissPageLoader = null;
    cardCleanups.forEach((cleanup) => cleanup());
    cardCleanups = [];
    scrollHost.replaceChildren(grid, sentinel, loader);
    grid.replaceChildren();
    loader.hidden = true;
    observer?.observe(sentinel);
    void loadMore();
  };

  let searchDebounce: ReturnType<typeof setTimeout> | null = null;
  searchInput.addEventListener('input', () => {
    if (searchDebounce) clearTimeout(searchDebounce);
    searchDebounce = setTimeout(() => resetAndReload(searchInput.value), 250);
  });

  observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((e) => e.isIntersecting)) void loadMore();
    },
    { root: null, rootMargin: '320px 0px' }
  );
  observer.observe(sentinel);
  window.addEventListener('resize', checkLoadMore, { passive: true });
  void loadMore();
}

function showPageLoading(root: HTMLElement, message: string): () => void {
  return mountEarthLoaderPage(root, message);
}

export function mountArchivesApp(root: HTMLElement, options: ArchivesMountOptions = {}): void {
  setArchivesEmbedded(options.embedded === true);
  const archiveId = parseReplayIdFromHash();

  destroyActiveReplay();

  if (archiveId != null) {
    const dismiss = showPageLoading(root, 'Chargement du replay…');
    void fetchRaceHistory(archiveId)
      .then((detail) => {
        dismiss();
        return renderDetail(root, detail);
      })
      .catch((err: unknown) => {
        dismiss();
        const msg =
          err instanceof Error ? err.message : "Cette course n'existe pas ou a été supprimée.";
        showArchiveLoadError(root, msg);
      });
    return;
  }

  void Promise.resolve()
    .then(() => {
      mountArchiveList(root, (err) => {
        showArchiveLoadError(
          root,
          err instanceof Error ? err.message : 'Erreur lors de l’affichage de la liste.'
        );
      });
    })
    .catch(() => {
      root.replaceChildren();
      const { page, body } = createPageShell({ title: 'Replays' });
      body.appendChild(
        createPageEmptyState({
          title: 'Impossible d’afficher les replays',
          description:
            'Le serveur du jeu ne répond pas pour l’instant. Réessayez dans quelques instants.',
          primaryAction: {
            label: 'Réessayer',
            onClick: () => mountArchivesApp(root),
          },
        })
      );
      root.appendChild(page);
    });
}
